import React, { useEffect, useState } from 'react'
import './AppraisalsPreview.scss'
import { BreadCrumb, ModalWrapper } from 'Components'
import { ApStepOne, ApStepTwo, ApStepThree } from 'Pages/Frontend/Components'
import { appraisalService } from 'Services/appraisalService'
import { useForm, SubmitHandler } from 'react-hook-form'
import { useSearchParams, useNavigate, useParams } from 'react-router-dom'
import Swal from 'sweetalert2'
import { AppraisalsType } from 'Models/appraisalsType'
import ApStepThreeEmployeePreview from '../../../../Components/Appraisals/ApStepThreeEmployeePreview'
import ApStepThreeOpen from 'Pages/Frontend/Components/Appraisals/ApStepThreeOpen'

export type AppraisalFormLMProps = {
  kpi: {
    metric: string
    target: number
    employee_score: number
    employee_comment?: string
    manager_score?: number
  }[]
  coreValues: {
    question: string
    category: string
    employee_score: number
    manager_score?: number
  }[]
  isDraft?: boolean
}

export type AppraisalFormProps = {
  kpi: {
    metric: string
    target: number
    employee_score: number
    employee_comment?: string
    manager_score?: number
  }[]
  coreValues: {
    question: string
    category: string
    employee_score: number
    manager_score?: number
  }[]
  personalAssessment: {
    category: string
    question: string
    answer: string
    manager_score?: number
  }[]
  lineManagerComment?: string
  isDraft?: boolean
}
const AppraisalsPreview = () => {
  const { apId, userId } = useParams<{ apId: string; userId: string }>()

  const [searchParams] = useSearchParams()
  const [request, setRequest] = useState(false)

  // Get a specific query parameter
  const step = searchParams.get('step')

  const [appraisal, setAppraisals] = useState<AppraisalsType>()
  // const currentStep = step ? parseInt(step) : 1; // Convert step to a number and default to 1 if invalid.

  const [currentStep, setCurrentStep] = useState(1)
  const navigate = useNavigate()

  const [showApprovalModal, setShowApprovalModal] = useState(false)

  const toggleApprovalModal = () => setShowApprovalModal(!showApprovalModal)

  const {
    register,
    handleSubmit,
    trigger,
    setValue,
    watch,
    formState: { errors },
    control,
  } = useForm<AppraisalFormProps>()

  const onSubmit: SubmitHandler<AppraisalFormProps> = async (data, e) => {
    // console.log('Form Data:', data)
    const action = (e?.nativeEvent as SubmitEvent)?.submitter as HTMLButtonElement

    const isDraft = action?.value === 'draft'
    const payload = {
      ...data,
      isDraft,
    }
    setRequest(true)
    await appraisalService.create(payload).then(
      () => {
        setRequest(false)
        Swal.fire({
          icon: 'success',
          title: `${isDraft ? 'Saved' : 'Completed'}`,
          text: `Appraisal ${isDraft ? 'saved' : 'submitted'} successfully!`,
        })

        setTimeout(() => {
          navigate('/appraisals')
        }, 1000)
      },
      (error) => {
        setRequest(false)
        Swal.close()
        // console.log('productService error', error.response)
        const swalWithBootstrapButtons = Swal.mixin({
          customClass: {
            cancelButton: 'btn btn-danger',
          },
          buttonsStyling: false,
        })

        swalWithBootstrapButtons.fire({
          title: 'Oops!',
          text: error.response.data.error || error.response.data.message,
          icon: 'warning',
          showCancelButton: true,
          showConfirmButton: false,
          cancelButtonText: 'close',
          reverseButtons: true,
        })
      },
    )
  }

  const handleNext = async () => {
    const isValid = await trigger() // Validate the current form fields
    if (isValid) {
      setCurrentStep((prev) => prev + 1)
      navigate(`/appraisal-preview/${apId}/${userId}?step=${currentStep + 1}`)
    } else {
      console.error('Form contains errors, cannot proceed to the next step.')
    }
  }

  const handlePrev = () => {
    if (currentStep > 1) {
      setCurrentStep((prev) => prev - 1)
      navigate(`/appraisal-preview/${apId}/${userId}?step=${currentStep - 1}`)
    }
  }

  const getAppraisals = async (apId: string) => {
    await appraisalService.getSingleAdmin(apId).then(
      (response) => {
        // console.log('response 45', response.data.data.dataset[0])
        setAppraisals(response.data.data.dataset[0])
      },
      (error: { response: { data: { error: string } } }) => {
        console.log('appraisalService error', error.response)
      },
    )
  }

  useEffect(() => {
    if (apId) getAppraisals(apId)
  }, [apId])

  useEffect(() => {
    if (appraisal) {
      // Use the proper template literal type for the field path
      const pLength = appraisal.personalAssessment.length
      for (let x = 0; x < pLength; x++) {
        setValue(`personalAssessment.${x}.answer`, appraisal.personalAssessment[x].answer)
      }

      const kpLength = appraisal.kpi.length
      for (let x = 0; x < kpLength; x++) {
        setValue(`kpi.${x}.employee_score`, appraisal.kpi[x].employee_score)
        setValue(`kpi.${x}.employee_comment`, appraisal.kpi[x].employee_comment)
      }

      const cvLength = appraisal.coreValues.length
      for (let x = 0; x < cvLength; x++) {
        setValue(`coreValues.${x}.employee_score`, appraisal.coreValues[x].employee_score)
      }
    }
  }, [appraisal])

  const [settings, setSettings] = useState<{
    id: number
    appraisalYear: number
    appraisalQuarter: number
    appraisalOpen: boolean
    payslipMonth: string
    payslipYear: any
    createdAt: string
  }>()

  const getAppraisalSettings = async () => {
    await appraisalService.settings().then(
      (res) => {
        // console.log('settings', res.data.data)
        setSettings(res.data.data)
      },
      (error) => {
        console.log(error)
      },
    )
  }

  useEffect(() => {
    getAppraisalSettings()
  }, [])

  const [activeAccordion, setActiveAccordion] = useState(1)
  const [inputError, setInputError] = useState(false)
  const [request2, setRequest2] = useState(false)

  const isOpen: boolean = appraisal?.status === 'DRAFT'
  return (
    <div className='AppraisalsPreview mb-5'>
      <div className='container'>
        <BreadCrumb
          title={'Q1’25 Appraisal Form'}
          parent={'Appraisal'}
          parentUrl={'appraisals'}
          showTitle={false}
        />
      </div>
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className='container mt-5'>
          <div className='section-top mb-4'>
            <div className='section-title'>
              Q{settings?.appraisalQuarter} {settings?.appraisalYear} Appraisal Form
            </div>
            <div className='section-description'>
              {step}
              SECTION A ({step === '' ? 1 : step} of 3)
            </div>
          </div>
          {currentStep === 1 && (
            <ApStepOne
              register={register}
              errors={errors}
              isManager={true}
              activeAccordion={activeAccordion}
              setActiveAccordion={setActiveAccordion}
              inputError={inputError}
              setInputError={setInputError}
              isDisabled={isOpen ? false : true}
            />
          )}
          {currentStep === 2 && (
            <ApStepTwo
              register={register}
              errors={errors}
              isManager={false}
              isAdmin={false}
              isDisabled={false}
              watch={watch}
              employeeId={userId}
              preview={isOpen ? false : true}
              control={control}
            />
          )}
          {/* 27650 */}

          {currentStep === 3 && isOpen && (
            <ApStepThreeOpen
              register={register}
              errors={errors}
              watch={watch}
              isAdmin={false}
              isManager={false}
              control={control}
            />
          )}
          {currentStep === 3 && !isOpen && (
            <ApStepThreeEmployeePreview
              register={register}
              errors={errors}
              isManager={false}
              isAdmin={false}
              isDisabled={false}
              watch={watch}
              preview={true}
            />
          )}

          <div className='row justify-content-end mt-3'>
            <div className='col-md-3 text-end'>
              {currentStep > 1 && (
                <button
                  type='button'
                  className='btn btn-default m-1 btn-navigate'
                  onClick={handlePrev}
                >
                  Prev
                </button>
              )}

              {currentStep < 3 && (
                <button
                  type='button'
                  className='btn btn-default m-1 btn-navigate'
                  onClick={handleNext}
                >
                  Next
                </button>
              )}

              {isOpen && currentStep === 3 ? (
                <button
                  className='btn btn-default m-1 btn-navigate'
                  type='submit'
                  disabled={request}
                  // onClick={() => toggleApprovalModal()}
                >
                  Submit {request && <span className='spinner-border spinner-border-sm'></span>}
                </button>
              ) : (
                ''
              )}
            </div>
          </div>
        </div>

        {/* <ModalWrapper
          showModal={showApprovalModal}
          closeModal={toggleApprovalModal}
          title="Line Manager's Comment"
        ></ModalWrapper> */}
      </form>
    </div>
  )
}

export default AppraisalsPreview
